import React, { useState, useEffect, useMemo } from 'react';
import { CalendarIcon, X } from 'lucide-react';

import {
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Input,
} from '@predoc/cow';
import { format, isSameDay } from 'date-fns';

import { formatDateInput, parseInputDate } from '@/utils/date';

const disablePastDates = (date: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

const disableFutureDates = (date: Date) => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return date > today;
};

export type DisableDatesOption = 'past' | 'future' | ((date: Date) => boolean);

export function DatePickerInput({
  onChange,
  placeholder = 'MM/DD/YYYY',
  value,
  disabled = false,
  disableDates,
  error = false,
  clearable = false,
}: {
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  value?: Date;
  disabled?: boolean;
  disableDates?: DisableDatesOption;
  error?: boolean;
  clearable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const initialValue = value ? format(value, 'MM/dd/yyyy') : '';
  const [inputValue, setInputValue] = useState(initialValue);
  const isToday = value ? isSameDay(value, new Date()) : false;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const rawValue = e.target.value;
    const isDeleting = rawValue.length < inputValue.length;

    const formattedValue = formatDateInput(rawValue, isDeleting);
    setInputValue(formattedValue);

    // to match MM/DD/YYYY format
    if (formattedValue.length === 10) {
      const parsedDate = parseInputDate(formattedValue);
      if (parsedDate) {
        onChange(parsedDate);
      }
    } else if (formattedValue === '') {
      onChange(undefined);
    }
  };

  const handleCalendarSelect = (date: Date | undefined) => {
    setOpen(false);
    if (date) {
      onChange(date);
      const formattedDate = format(date, 'MM/dd/yyyy');
      setInputValue(formattedDate);
    } else {
      onChange(undefined);
      setInputValue('');
    }
  };

  const handleCalendarIconClick = (e: React.MouseEvent) => {
    if (disabled) return;
    e.stopPropagation();
    setOpen(true);
  };

  useEffect(() => {
    if (value) {
      const formattedDate = format(value, 'MM/dd/yyyy');
      setInputValue(formattedDate);
    } else if (!value && inputValue) {
      setInputValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleOpenChange = (newOpen: boolean) => {
    if (disabled && newOpen) return;
    setOpen(newOpen);
  };

  const shouldDisableDate: ((date: Date) => boolean) | undefined =
    useMemo(() => {
      // By default, do not disable any dates.
      if (!disableDates) return undefined;

      if (typeof disableDates === 'string') {
        if (disableDates === 'past') {
          return disablePastDates;
        }
        if (disableDates === 'future') {
          return disableFutureDates;
        }
      }

      if (typeof disableDates === 'function') {
        return disableDates;
      }

      return disablePastDates;
    }, [disableDates]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={true}>
      <div className="relative flex items-center">
        <PopoverTrigger asChild>
          <CalendarIcon
            className={`text-muted-foreground absolute left-3 z-10 h-4 w-4 ${
              disabled ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
            onClick={handleCalendarIconClick}
          />
        </PopoverTrigger>
        <Input
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          className={`pl-10 ${clearable && inputValue ? 'pr-8' : ''} ${error ? 'border-destructive focus-visible:ring-destructive' : ''}`}
          maxLength={10}
          disabled={disabled}
        />
        {clearable && inputValue && !disabled ? (
          <X
            className="text-muted-foreground absolute right-2 z-10 h-4 w-4 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleCalendarSelect(undefined);
            }}
          />
        ) : null}
        {isToday ? (
          <span className="pointer-events-none absolute left-[calc(2.5rem+10ch+0.25rem)] z-10 flex h-full items-center text-sm">
            (Today)
          </span>
        ) : null}
      </div>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          captionLayout="dropdown"
          selected={value}
          onSelect={handleCalendarSelect}
          disabled={shouldDisableDate}
        />
      </PopoverContent>
    </Popover>
  );
}
